import { format } from "date-fns";
import type { AgendaSettings, AppointmentStatus } from "@bookdental/schemas";
import { minutesToTime, timeToMinutes } from "@/lib/agenda-time";
import type { ChipService } from "../ServiceChips";

export type DialogInitial = {
  id?: string;
  date: Date;
  startTime: string;
  endTime: string;
  patientId?: string | null;
  patientName?: string;
  patientPhone?: string;
  dentistId?: string | null;
  dentistName?: string;
  /** Vínculo estruturado da consulta (AppointmentService), na ordem de seleção. */
  serviceIds?: string[];
  procedure?: string;
  status?: AppointmentStatus;
  notes?: string;
  sendReminder?: boolean;
  color?: string | null;
  salaId?: string | null;
  /** A sala que a consulta já tem — mesmo desativada, continua escolhível só pra ela. */
  sala?: { id: string; nome: string; cor: string | null } | null;
};

export type FormValues = {
  patient: { id: string | null; name: string; phone: string };
  dentistId: string | null;
  dentistName: string;
  /** Serviços escolhidos, na ordem. Vem do vínculo real (AppointmentService) — antes da unificação
   *  isso era reconstruído por match de nome no texto de `procedure`, o que quebrava silenciosamente
   *  quando alguém renomeava um procedimento. */
  serviceIds: string[];
  /** Texto livre. Fica sincronizado com os serviços quando há seleção, mas pode ser editado sozinho
   *  (organização sem catálogo, ou descrição que não corresponde a nenhum item). */
  procedure: string;
  procedureColor: string | null;
  date: string;
  startTime: string;
  endTime: string;
  status: AppointmentStatus;
  notes: string;
  sendReminder: boolean;
  salaId: string | null;
};

const PROCEDURE_JOIN = " · ";

export function initialFormValues(i: DialogInitial, s: AgendaSettings): FormValues {
  return {
    patient: { id: i.patientId ?? null, name: i.patientName ?? "", phone: i.patientPhone ?? "" },
    dentistId: i.dentistId ?? null,
    dentistName: i.dentistName ?? "",
    serviceIds: i.serviceIds ?? [],
    procedure: i.procedure ?? "",
    procedureColor: i.color ?? null,
    date: format(i.date, "yyyy-MM-dd"),
    startTime: i.startTime,
    endTime: i.endTime,
    status: i.status ?? "SCHEDULED",
    notes: i.notes ?? "",
    sendReminder: i.sendReminder ?? s.whatsappReminderDefault,
    salaId: i.salaId ?? null,
  };
}

const resolver = (ids: string[], servicos: ChipService[]) =>
  ids.map((id) => servicos.find((s) => s.id === id)).filter((s): s is ChipService => !!s);

/** Texto que uma seleção geraria — pra saber se o que está no campo veio dos serviços ou foi digitado. */
export const textoDosServicos = (ids: string[], servicos: ChipService[]) =>
  resolver(ids, servicos)
    .map((s) => s.name)
    .join(PROCEDURE_JOIN);

/** Nova seleção de serviços: sincroniza o texto, a duração e a cor do card. */
export function aplicarSelecao(v: FormValues, proximos: string[], servicos: ChipService[], fechamento: string): Partial<FormValues> {
  const escolhidos = resolver(proximos, servicos);
  // Só sobrescreve/limpa o texto quando ele veio dos próprios serviços. Se o usuário digitou algo
  // por cima, desmarcar tudo não apaga o que ele escreveu.
  const textoVeioDosServicos = v.procedure.trim() === "" || v.procedure === textoDosServicos(v.serviceIds, servicos);
  // Duração = MAX (não soma) — procedimentos rodam em paralelo na mesma cadeira, o card ocupa só o maior.
  const maior = escolhidos.reduce((acc, s) => Math.max(acc, s.durationMinutes ?? 0), 0);
  const fim = maior > 0 ? minutesToTime(Math.min(timeToMinutes(v.startTime) + maior, timeToMinutes(fechamento))) : v.endTime;
  return {
    serviceIds: proximos,
    procedure: textoVeioDosServicos ? escolhidos.map((s) => s.name).join(PROCEDURE_JOIN) : v.procedure,
    // Cor: o primeiro escolhido — identidade visual estável quando se adiciona/remove itens.
    procedureColor: escolhidos[0]?.color ?? null,
    endTime: fim,
  };
}
